/**
 * useAuth — Supabase Auth hook for FairPath.
 *
 * Provides sign-up, sign-in, sign-out, password reset, and session state.
 * Listens to onAuthStateChange so the UI reacts immediately when auth state
 * changes (login, logout, token refresh).
 *
 * Usage (via AuthContext — don't call this directly):
 *   const { user, isAuthenticated, signIn, signOut } = useAuthContext();
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { beginAnalyticsAudienceResolution, resolveAnalyticsAudience } from "../lib/analyticsAudience";
import { supabase } from "../lib/supabase";
import { identifyUser, resetUser } from "../lib/posthog";
import { sendEmail } from "../services/email";
import type { User, Session } from "@supabase/supabase-js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UserProfile {
  id: string;
  email: string;
  full_name: string | null;
  phone: string | null;
  created_at: string;
}

export interface AuthState {
  user: User | null;
  profile: UserProfile | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
}

export interface AuthActions {
  signUp: (
    email: string,
    password: string,
    fullName?: string
  ) => Promise<{ error: string | null; needsConfirmation: boolean }>;
  signIn: (
    email: string,
    password: string
  ) => Promise<{ error: string | null }>;
  signInWithGoogle: () => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: string | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: string | null }>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAuth(): AuthState & AuthActions {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const audienceUserRef = useRef<string | null | undefined>(undefined);

  // ── Initialize: restore session on mount + listen for auth changes ─────
  // CRITICAL: No async Supabase data queries inside onAuthStateChange.
  // Calling supabase.from().select() inside the callback creates a global
  // deadlock — all subsequent Supabase queries hang forever. State updates
  // only inside the callback; data fetching in a separate useEffect.
  useEffect(() => {
    let mounted = true;
    let authEventObserved = false;
    const applySession = (nextSession: Session | null) => {
      const nextUserId = nextSession?.user.id ?? null;
      if (audienceUserRef.current !== nextUserId) {
        beginAnalyticsAudienceResolution();
        audienceUserRef.current = nextUserId;
      }
      setSession(nextSession);
      setUser(nextSession?.user ?? null);
      setIsLoading(false);
    };

    // Restore session synchronously first so auth state is available
    // before any data queries run.
    supabase.auth.getSession().then(({ data: { session: currentSession }, error: sessionError }) => {
      if (!mounted || authEventObserved) return;
      if (sessionError) {
        resolveAnalyticsAudience(beginAnalyticsAudienceResolution(), null);
        setIsLoading(false);
        return;
      }
      applySession(currentSession);
    }).catch((err) => {
      console.error("[useAuth] Session init error:", err);
      if (mounted && !authEventObserved) {
        resolveAnalyticsAudience(beginAnalyticsAudienceResolution(), null);
        setIsLoading(false);
      }
    });

    // Listen for auth state changes — state updates ONLY, no data queries.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (!mounted) return;
      // auth-js also emits INITIAL_SESSION/null after a restoration error.
      // Only the explicit getSession result may establish bootstrap success.
      if (event === "INITIAL_SESSION") return;
      authEventObserved = true;
      applySession(newSession);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Use the existing trusted founder boolean. No identity enters analytics state,
  // and no Supabase query runs inside the auth callback (which would deadlock).
  useEffect(() => {
    if (isLoading || audienceUserRef.current === undefined) return;
    const generation = beginAnalyticsAudienceResolution();
    if (!user) { resolveAnalyticsAudience(generation, false); return; }
    let cancelled = false;
    void Promise.resolve(supabase.rpc("is_admin")).then(({ data, error: rpcError }) => {
      if (!cancelled) resolveAnalyticsAudience(generation, !rpcError && typeof data === "boolean" ? data : null);
    }).catch(() => { if (!cancelled) resolveAnalyticsAudience(generation, null); });
    return () => { cancelled = true; };
  }, [user?.id, isLoading]);

  // ── Fetch profile when user changes ──────────────────────────────────
  // Runs OUTSIDE onAuthStateChange to avoid the Supabase deadlock.
  // Profile is nice-to-have (full_name, phone). If it fails, we proceed
  // with user data from the Supabase JWT (user.email, user.id).
  useEffect(() => {
    if (!user) {
      setProfile(null);
      return;
    }

    let cancelled = false;

    const fetchProfile = async () => {
      try {
        const { data, error: profileError } = await supabase
          .from("profiles")
          .select("id, email, full_name, phone, created_at")
          .eq("id", user.id)
          .single();

        if (cancelled) return;

        if (profileError) {
          console.warn("[useAuth] Failed to fetch profile:", profileError.message);
          // Still identify with basic user info (hashed email)
          identifyUser(user.id, { email: user.email });
          return;
        }
        setProfile(data as UserProfile);

        // PostHog: identify user with profile data (hashed email)
        identifyUser(user.id, {
          email: user.email,
          created_at: (data as UserProfile).created_at ?? undefined,
        });
      } catch (err) {
        if (!cancelled) {
          console.warn("[useAuth] Profile fetch exception:", err);
        }
      }
    };

    fetchProfile();

    return () => { cancelled = true; };
  }, [user?.id]);

  // ── signUp ─────────────────────────────────────────────────────────────
  const signUp = useCallback(
    async (
      email: string,
      password: string,
      fullName?: string
    ): Promise<{ error: string | null; needsConfirmation: boolean }> => {
      setError(null);

      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { full_name: fullName ?? null },
        },
      });

      if (signUpError) {
        const msg = mapAuthError(signUpError.message);
        setError(msg);
        return { error: msg, needsConfirmation: false };
      }

      // If email confirmation is enabled, user will have identities but
      // session may be null until they confirm.
      const needsConfirmation =
        data.user !== null &&
        (data.session === null || data.user.identities?.length === 0);

      // If full_name was provided and we have a user, update the profile
      if (fullName && data.user) {
        await supabase
          .from("profiles")
          .update({ full_name: fullName })
          .eq("id", data.user.id);
      }

      // Welcome email — fire-and-forget, never blocks signup flow.
      // Checks welcome_email_sent flag first to prevent duplicate from stripe-webhook.
      // Fail-open: if profile fetch fails, send anyway (missing welcome > duplicate).
      if (data.user) {
        const { data: flagRow, error: flagReadErr } = await supabase
          .from("profiles")
          .select("welcome_email_sent")
          .eq("id", data.user.id)
          .single();

        if (flagReadErr) {
          console.warn("[useAuth] Could not read welcome_email_sent — sending anyway (fail-open):", flagReadErr.message);
        }

        if (!flagRow?.welcome_email_sent) {
          sendEmail("welcome", email, { fullName: fullName ?? undefined });
          supabase
            .from("profiles")
            .update({ welcome_email_sent: true, last_email_sent_at: new Date().toISOString() })
            .eq("id", data.user.id)
            .then(({ error: flagErr }) => {
              if (flagErr) console.warn("[useAuth] Failed to set welcome_email_sent:", flagErr.message);
            });
        }
      }

      return { error: null, needsConfirmation };
    },
    []
  );

  // ── signIn ─────────────────────────────────────────────────────────────
  const signIn = useCallback(
    async (
      email: string,
      password: string
    ): Promise<{ error: string | null }> => {
      setError(null);

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        const msg = mapAuthError(signInError.message);
        setError(msg);
        return { error: msg };
      }

      return { error: null };
    },
    []
  );

  // ── signInWithGoogle ──────────────────────────────────────────────────
  const signInWithGoogle = useCallback(async (): Promise<{ error: string | null }> => {
    setError(null);

    // Save the current full URL so /auth/callback can redirect back here.
    // Callers can override by writing to fp_auth_redirect BEFORE invoking.
    if (!localStorage.getItem("fp_auth_redirect")) {
      localStorage.setItem("fp_auth_redirect", window.location.href);
    }

    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (oauthError) {
      localStorage.removeItem("fp_auth_redirect");
      const msg = mapAuthError(oauthError.message);
      setError(msg);
      return { error: msg };
    }

    // OAuth redirects — no session update happens here.
    // The onAuthStateChange listener in /auth/callback picks it up on return.
    return { error: null };
  }, []);

  // ── signOut ────────────────────────────────────────────────────────────
  const signOut = useCallback(async () => {
    setError(null);
    await supabase.auth.signOut();

    // PostHog: reset user identity on sign out
    resetUser();

    // Belt-and-suspenders: clear any leftover Supabase auth tokens from
    // localStorage. The JS client occasionally leaves stale tokens.
    Object.keys(localStorage)
      .filter((key) => key.startsWith("sb-") && key.endsWith("-auth-token"))
      .forEach((key) => localStorage.removeItem(key));

    setUser(null);
    setProfile(null);
    setSession(null);
  }, []);

  // ── resetPassword ──────────────────────────────────────────────────────
  const resetPassword = useCallback(
    async (email: string): Promise<{ error: string | null }> => {
      setError(null);

      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        email,
        { redirectTo: `${window.location.origin}/reset-password` }
      );

      if (resetError) {
        const msg = mapAuthError(resetError.message);
        setError(msg);
        return { error: msg };
      }

      return { error: null };
    },
    []
  );

  // ── updatePassword ─────────────────────────────────────────────────────
  const updatePassword = useCallback(
    async (newPassword: string): Promise<{ error: string | null }> => {
      setError(null);

      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        const msg = mapAuthError(updateError.message);
        setError(msg);
        return { error: msg };
      }

      return { error: null };
    },
    []
  );

  return {
    user,
    profile,
    session,
    isLoading,
    isAuthenticated: !!session && !!user,
    error,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resetPassword,
    updatePassword,
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Map Supabase error messages to user-friendly strings. */
function mapAuthError(message: string): string {
  const lower = message.toLowerCase();
  if (lower.includes("invalid login credentials"))
    return "Invalid email or password.";
  if (lower.includes("email not confirmed"))
    return "Please check your email and confirm your account before signing in.";
  if (lower.includes("user already registered"))
    return "An account with this email already exists.";
  if (lower.includes("password") && lower.includes("6"))
    return "Password must be at least 6 characters.";
  if (lower.includes("rate limit") || lower.includes("too many"))
    return "Too many attempts. Please wait a moment and try again.";
  if (lower.includes("network") || lower.includes("fetch"))
    return "Network error. Please check your connection and try again.";
  return message;
}
